// src/lib/gcal.ts
// Public Google Calendar fetch (no OAuth). Uses API key + public calendars.
console.log("ENV LOADED?", {
  PADEL1: import.meta.env.VITE_GCAL_PADEL1_ID,
  TENNIS1: import.meta.env.VITE_GCAL_TENNIS1_ID,
  API_KEY: import.meta.env.VITE_GCAL_API_KEY?.slice(0, 8) + "..."
});


import { type CourtKey, CANONICAL_KEYS } from './courts';

type GCalEvent = {
  id: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  summary?: string;
};

const API_KEY = import.meta.env.VITE_GCAL_API_KEY as string;

// Court calendar IDs from .env
export const CAL_PADEL1  = import.meta.env.VITE_GCAL_PADEL1_ID as string | undefined;
export const CAL_PADEL2  = import.meta.env.VITE_GCAL_PADEL2_ID as string | undefined;
export const CAL_TENNIS1 = import.meta.env.VITE_GCAL_TENNIS1_ID as string | undefined;
export const CAL_TENNIS2 = import.meta.env.VITE_GCAL_TENNIS2_ID as string | undefined;
export const CAL_TENNIS3 = import.meta.env.VITE_GCAL_TENNIS3_ID as string | undefined;

// Map canonical court keys to calendar IDs
export const COURT_CAL_MAP: Record<CourtKey, string | undefined> = {
  "padel-1":  CAL_PADEL1,
  "padel-2":  CAL_PADEL2,
  "tennis-1": CAL_TENNIS1,
  "tennis-2": CAL_TENNIS2,
  "tennis-3": CAL_TENNIS3,
};

export function getCalendarIdForCourt(courtKey: CourtKey) {
  // Dev-only validation: warn if courtKey isn't one of the 5 canonical keys
  if (import.meta.env.DEV && !CANONICAL_KEYS.includes(courtKey)) {
    console.warn('[getCalendarIdForCourt] Invalid court key (not canonical):', courtKey);
  }

  const id = COURT_CAL_MAP[courtKey];

  if (import.meta.env.DEV) {
    console.debug('[COURT]', courtKey, 'calendarId', !!id);
  }

  if (!id) {
    console.warn("No calendar ID for court:", courtKey);
  }

  return id!;
}

// ---- Date helpers ----
function dayWindowISO(day: string | Date) {
  const d = typeof day === "string" ? new Date(`${day}T00:00:00`) : new Date(day);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const end   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
  return { timeMin: start.toISOString(), timeMax: end.toISOString() };
}

function eventBoundsMs(ev: GCalEvent) {
  const s =
    ev.start.dateTime ?? (ev.start.date ? `${ev.start.date}T00:00:00` : undefined);
  const e =
    ev.end.dateTime ?? (ev.end.date ? `${ev.end.date}T23:59:59` : undefined);
  return { startMs: s ? Date.parse(s) : 0, endMs: e ? Date.parse(e) : 0 };
}

// ---- Core fetchers ----
export async function fetchEventsForDay(
  calendarId: string,
  day: string | Date
): Promise<GCalEvent[]> {
  if (!API_KEY) {
    console.error("VITE_GCAL_API_KEY is missing");
    return [];
  }
  if (!calendarId) {
    console.error("fetchEventsForDay: calendarId is empty");
    return [];
  }

  const { timeMin, timeMax } = dayWindowISO(day);
  const params = new URLSearchParams({
    key: API_KEY,
    singleEvents: "true", // expand recurring
    orderBy: "startTime",
    timeMin,
    timeMax,
    maxResults: "250",
  });

  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
    calendarId
  )}/events?${params.toString()}`;

  const res = await fetch(url);
  if (!res.ok) {
    const txt = await res.text();
    console.error("Google Calendar API error", res.status, txt);
    return [];
  }
  const data = (await res.json()) as { items?: GCalEvent[] };
  return data.items ?? [];
}

export async function fetchEventsForDayFromMany(
  calendarIds: Array<string | undefined>,
  day: string | Date
): Promise<GCalEvent[]> {
  const ids = calendarIds.filter((x): x is string => !!x);
  const lists = await Promise.all(ids.map((id) => fetchEventsForDay(id, day)));
  return lists.flat();
}

// ---- Slot logic ----
export function isSlotBooked(
  slotStartISO: string,
  slotEndISO: string,
  events: GCalEvent[]
) {
  const s = Date.parse(slotStartISO);
  const e = Date.parse(slotEndISO);

  const result = events.some((ev) => {
    const { startMs, endMs } = eventBoundsMs(ev);
    const overlaps = startMs < e && endMs > s;

    if (overlaps) {
      console.log(`[isSlotBooked] OVERLAP FOUND:`, {
        slotStart: slotStartISO,
        slotEnd: slotEndISO,
        eventStart: ev.start.dateTime || ev.start.date,
        eventEnd: ev.end.dateTime || ev.end.date,
        eventSummary: ev.summary,
        slotStartMs: s,
        slotEndMs: e,
        eventStartMs: startMs,
        eventEndMs: endMs
      });
    }

    return overlaps;
  });

  return result;
}

export function buildBookedSet(
  slots: Array<{ startISO: string; endISO: string }>,
  events: GCalEvent[]
) {
  const booked = new Set<string>();
  for (const sl of slots) {
    if (isSlotBooked(sl.startISO, sl.endISO, events)) {
      booked.add(`${sl.startISO}|${sl.endISO}`);
    }
  }
  return booked;
}

// ---- Debug helper ----
export function debugBrowserURL(calendarId: string, day: string | Date) {
  const { timeMin, timeMax } = dayWindowISO(day);
  const params = new URLSearchParams({
    key: API_KEY,
    singleEvents: "true",
    orderBy: "startTime",
    timeMin,
    timeMax,
    maxResults: "50",
  });
  return `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
    calendarId
  )}/events?${params.toString()}`;
}
